import { Button } from "../../../ui/Button";
import { LAYOUT, TEXT_STYLES } from "../../../constants";
import { RunState, newRun, healPlayer, isAtMaxFloor } from "../../../state/runState";
import { EncounterScene } from "../../EncounterScene";
import { BaseScreen } from "../../../screens/BaseScreen";
import { createRunInfoPanel } from "../../../screens/uiHelpers";
import { tagTextForAutomation } from "../../../automation/automationTags";
import { OVERWORLD_SCENE_KEY } from "../../OverworldScene";
import { OverworldState, newOverworldState } from "../../../state/overworldState";
import { advanceToNextFloor } from "../../../state/floorAdvancement";
import { refreshZonesForNewFloor, resetOverworldAfterReward } from "../../../state/overworldLogic";
import { navLog } from "../../../debugNavigation";
import type { ScreenContext } from "../../../screens/types";

export class RewardScreen extends BaseScreen {
  private run!: RunState;
  private overworldState?: OverworldState;

  constructor(scene: EncounterScene) {
    super(scene, "REWARD");
  }

  enter(context?: ScreenContext) {
    this.run = context?.run ?? newRun();
    this.overworldState = context?.overworldState ?? this.scene.getOverworldState?.();

    // Heal player to full HP
    healPlayer(this.run, 1.0); // Full heal

    const { width, height } = this.scene.scale;

    const title = this.scene.add
      .text(width / 2, LAYOUT.screenPadding, "Floor Complete!", TEXT_STYLES.title)
      .setOrigin(0.5, 0);
    this.addToContainer(title);
    tagTextForAutomation(title, "reward-title");

    const subtitle = this.scene.add
      .text(
        width / 2,
        LAYOUT.screenPadding + 56,
        "You have been fully healed!",
        TEXT_STYLES.subtitle
      )
      .setOrigin(0.5, 0);
    this.addToContainer(subtitle);
    tagTextForAutomation(subtitle, "reward-subtitle");

    const panel = createRunInfoPanel(
      this.scene,
      this.run,
      LAYOUT.screenPadding,
      height * 0.22,
      width - LAYOUT.screenPadding * 2
    );
    this.addToContainer(panel);

    const buttonWidth = width - LAYOUT.screenPadding * 2;

    // Determine button label based on whether we're at max floor
    const isMaxFloor = isAtMaxFloor(this.run);
    const buttonLabel = isMaxFloor ? "Continue" : "Continue to Next Floor";

    const continueButton = new Button(this.scene, LAYOUT.screenPadding, height * 0.7, {
      width: buttonWidth,
      height: LAYOUT.buttonHeight,
      label: buttonLabel,
      automationName: "continue-to-next-floor",
      onClick: () => {
        this.continueToNextFloor();
      },
    });
    this.addToContainer(continueButton);
  }

  private continueToNextFloor() {
    const startedFloor = this.run.floor;

    // Advance to next floor if not at max
    if (!isAtMaxFloor(this.run)) {
      try {
        advanceToNextFloor(this.run);
      } catch (error) {
        // If we can't advance (e.g., already at max), just return to overworld
        console.warn("Failed to advance floor:", error);
      }
    }

    // Clear boss encounter flag
    this.run.isBossEncounter = false;

    // Update overworld state with current run
    const overworldState = this.overworldState ?? newOverworldState(this.run);
    overworldState.run = this.run;
    resetOverworldAfterReward(overworldState);

    // If we advanced floors, reset completed zones so the new floor spawns fresh encounters.
    if (this.run.floor !== startedFloor) {
      refreshZonesForNewFloor(overworldState);
    }

    // Return to overworld
    this.scene.overlay.transition(() => {
      navLog("RewardScreen", "Starting overworld after floor reward.", {
        floor: this.run.floor,
        isBossEncounter: Boolean(this.run.isBossEncounter),
      });
      this.scene.scene.start(OVERWORLD_SCENE_KEY, {
        overworldState,
      });
    });
  }
}
